/**
 * "Miete: Erwartet vs. Fakturiert vs. Bezahlt" — the page's main series.
 *
 * The month axis is B2's expected history, because it is the only source that
 * yields a figure for a month with no invoices at all. A month with no
 * ledger-attributed invoice therefore shows explicit zeros for the two actuals,
 * which means "nothing is PROVEN to belong to this month" — not "no invoice
 * exists". The subtitle says so on screen rather than in this comment only.
 *
 * WHY THE SUBTITLE IS NOT DECORATION.
 * `paid_current` is as-of-now for EVERY point, including old ones: paying a
 * year-old invoice today moves that year-old point upward. That is correct, and
 * it is exactly why this series must never be read as "cash per month". The
 * dated view is `cashflow_trend`, and it lives in its own card on its own axis.
 *
 * The window control changes `trend_months` and refetches. The backend caps it
 * at 12 and computes each month with the same canonical calculator, so asking
 * for more months is a different request — not a longer slice of this one.
 *
 * THE FOOTNOTE IS ABOUT THE SERIES, NOT ABOUT ANY ONE MONTH (UX-1).
 * Two actual lines flat on zero while the portfolio plainly holds invoices is the
 * single most confusing thing on this page, and the honest explanation is a
 * property of the SERIES: these lines only ever contain invoices whose rent month
 * the ledger proves. The org-wide unattributed count may be named beside that —
 * an unattributed invoice belongs to no rent month, so it provably appears in NO
 * point of this curve, and saying so is the opposite of assigning it to one.
 *
 * What must NOT happen here is a per-point verdict. The response carries no
 * exclusion count per trend month, so nothing in this component may mark a single
 * month as incomplete, and the org-wide number must never be filtered, split or
 * apportioned across the visible months.
 */
import React from "react";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import {
  compactAxisTick,
  fmtMoney,
  SERIES_COLOR,
  shortPeriodLabel,
  toChartNumber,
} from "./revenueAnalyticsFormat";

export const TREND_WINDOW_OPTIONS = [
  { value: 3, label: "Letzte 3 Monate" },
  { value: 6, label: "Letzte 6 Monate" },
  { value: 12, label: "Letzte 12 Monate" },
];

/**
 * The three series, exported so the legend, the tooltip and the accessible table
 * are provably reading ONE set of names.
 *
 * "Bezahlt (Stand heute)" carries its qualifier in the name itself rather than in
 * a caption, because the name is what the tooltip prints beside the figure — and
 * "Bezahlt" alone next to a month would read as money received in that month,
 * which is the other axis entirely (`cashflow_trend`).
 */
export const TREND_SERIES = [
  { key: "expected", name: "Erwartete Miete", color: SERIES_COLOR.expected, dash: undefined },
  { key: "invoiced", name: "Fakturiert", color: SERIES_COLOR.invoiced, dash: "5 4" },
  { key: "paid", name: "Bezahlt (Stand heute)", color: SERIES_COLOR.paid, dash: undefined },
];

function TrendTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-border bg-card px-3 py-2 text-xs shadow-md">
      <div className="mb-1 font-semibold text-foreground">{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center justify-between gap-4">
          <span className="text-muted-foreground">{p.name}</span>
          {/* The ORIGINAL contract string, not the charted Number. */}
          <span className="font-medium text-foreground">{fmtMoney(p.payload?.raw?.[p.dataKey])}</span>
        </div>
      ))}
    </div>
  );
}

/** What the two actual lines can and cannot contain. Never per month. */
function AttributionFootnote({ unattributedInvoiceCount }) {
  const n = unattributedInvoiceCount;
  return (
    <p className="mt-2 text-xs text-muted-foreground">
      Fakturiert und Bezahlt zeigen nur Rechnungen, die einem Mietmonat autoritativ
      zugeordnet sind.
      {n > 0 ? (
        <>
          {" "}
          Org-weit sind {n} {n === 1 ? "Rechnung" : "Rechnungen"} keinem Mietmonat
          zugeordnet; sie erscheinen deshalb in keinem Monat dieser Kurve.
        </>
      ) : null}
    </p>
  );
}

export default function RevenueTrendChart({
  trend,
  trendMonths,
  onTrendMonthsChange,
  unattributedInvoiceCount = 0,
}) {
  const data = (trend || []).map((p) => ({
    label: shortPeriodLabel(p),
    expected: toChartNumber(p?.expected_recurring_rent),
    invoiced: toChartNumber(p?.invoiced_total),
    paid: toChartNumber(p?.paid_current),
    // Kept beside the charted numbers so the tooltip can print the untouched
    // Decimal strings instead of a re-formatted float.
    raw: {
      expected: p?.expected_recurring_rent,
      invoiced: p?.invoiced_total,
      paid: p?.paid_current,
    },
  }));

  return (
    <div className="flex h-full flex-col rounded-xl border border-border bg-card p-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold text-foreground">
            Miete: Erwartet vs. Fakturiert vs. Bezahlt
          </h2>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Bezahlt = aktueller Zahlungsstand je Mietmonat, nicht Zahlungseingang im Monat.
          </p>
        </div>
        <select
          aria-label="Zeitraum des Verlaufs"
          value={trendMonths}
          onChange={(e) => onTrendMonthsChange(Number(e.target.value))}
          className="shrink-0 cursor-pointer rounded-lg border border-input bg-card px-2 py-1 text-xs text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {TREND_WINDOW_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      {data.length === 0 ? (
        <p className="mt-6 text-sm text-muted-foreground">
          Für diesen Zeitraum liegen keine Verlaufsdaten vor.
        </p>
      ) : (
        <>
          <div className="mt-3 h-[230px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data} margin={{ top: 6, right: 8, bottom: 0, left: -12 }}>
                <CartesianGrid
                  stroke="currentColor"
                  className="text-border"
                  strokeDasharray="3 4"
                  vertical={false}
                />
                <XAxis
                  dataKey="label"
                  tickLine={false}
                  axisLine={false}
                  tick={{ fontSize: 11, fill: "currentColor" }}
                  className="text-muted-foreground"
                />
                <YAxis
                  tickFormatter={compactAxisTick}
                  tickLine={false}
                  axisLine={false}
                  tick={{ fontSize: 11, fill: "currentColor" }}
                  className="text-muted-foreground"
                />
                <Tooltip content={<TrendTooltip />} />
                <Legend
                  verticalAlign="bottom"
                  height={28}
                  wrapperStyle={{ fontSize: 12 }}
                />
                {TREND_SERIES.map((s) => (
                  <Line
                    key={s.key}
                    type="monotone"
                    dataKey={s.key}
                    name={s.name}
                    stroke={s.color}
                    strokeWidth={2}
                    strokeDasharray={s.dash}
                    dot={{ r: 3 }}
                    isAnimationActive={false}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>

          <AttributionFootnote unattributedInvoiceCount={unattributedInvoiceCount} />

          {/* The chart must not be the only way to reach these numbers: recharts
              tooltips are mouse-only, so the same figures exist as text. */}
          <table className="sr-only">
            <caption>Miete erwartet, fakturiert und bezahlt je Monat</caption>
            <thead>
              <tr>
                <th scope="col">Monat</th>
                {TREND_SERIES.map((s) => (
                  <th key={s.key} scope="col">
                    {s.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.map((row) => (
                <tr key={row.label}>
                  <th scope="row">{row.label}</th>
                  {TREND_SERIES.map((s) => (
                    <td key={s.key}>{fmtMoney(row.raw[s.key])}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}
